/**
 * Creates a quadratic Bézier curve between two points.
 *
 * @param {number} sx - Source x
 * @param {number} sy - Source y
 * @param {number} tx - Target x
 * @param {number} ty - Target y
 * @param {number} curvature - Strength of curve (default 0.3)
 * @returns {string} SVG path command
 */
export function getBezierPath(sx, sy, tx, ty, curvature = 0.1) {
  // Midpoint of the straight line between shortened source and target
  const mx = (sx + tx) / 2;
  const my = (sy + ty) / 2;

  // Sag proportional to distance between nodes
  const dx = tx - sx;
  const dy = ty - sy;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Control point is adjusted according to the new midpoint and curvature
  let cx = mx;
  let cy = my + distance * curvature;

  // If the line is almost horizontal, keep y-coordinates the same but shift it downward
  if (Math.abs(dy) < 0.1) {
    cy = my + 5; // Shift the control point downward by 5 units (adjust this value)
  }

  return `M ${sx} ${sy} Q ${cx} ${cy} ${tx} ${ty}`;
}

export const shortenBezierEdge = (source, target, shortenAmount = 120) => {
  // Calculate distance between source and target
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Calculate unit direction vector
  const unitDx = dx / distance;
  const unitDy = dy / distance;

  // Shorten the edge by the fixed pixel amount
  const pixelShortenAmount = Math.min(distance, shortenAmount); // Ensure we don't shorten more than the total distance

  // Calculate new shortened points
  let hitSource = {
    x: source.x + pixelShortenAmount * unitDx,
    y: source.y + pixelShortenAmount * unitDy,
  };

  let hitTarget = {
    x: target.x - pixelShortenAmount * unitDx,
    y: target.y - pixelShortenAmount * unitDy,
  };

  // If the line is nearly horizontal, keep y-coordinates the same
  if (Math.abs(dy) < 0.1) {
    hitSource.y = source.y;
    hitTarget.y = target.y;
  }

  // If the line is nearly vertical, keep x-coordinates the same
  if (Math.abs(dx) < 0.1) {
    hitSource.x = source.x;
    hitTarget.x = target.x;
  }

  // Return the shortened hit points
  return [hitSource, hitTarget];
};

// Main function that combines shortening and curvature logic
export const getCurvedBezierPath = (
  source,
  target,
  shortenAmount = 20,
  curvature = 0.08,
) => {
  // Step 1: Shorten the source and target points by fixed pixel amount
  const [hitSource, hitTarget] = shortenBezierEdge(
    source,
    target,
    shortenAmount,
  );

  // Step 2: Generate the Bézier path using the shortened points and curvature
  return getBezierPath(
    hitSource.x,
    hitSource.y,
    hitTarget.x,
    hitTarget.y,
    curvature,
  );
};

/**
 * Returns true if the edge should appear faded when a node is hovered.
 */
export function isDimmedEdge(edge, hoveredNodeId) {
  if (!hoveredNodeId) return false;
  return edge.source !== hoveredNodeId && edge.target !== hoveredNodeId;
}
